import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider } from "@/context/AppContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { QuickEntryDrawer } from "@/components/QuickEntryDrawer";
import { AIInsightsDrawer } from "@/components/AIInsightsDrawer";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import DeliveryPartners from "@/pages/DeliveryPartners";
import DPProfile from "@/pages/DPProfile";
import DailyOperations from "@/pages/DailyOperations";
import Earnings from "@/pages/Earnings";
import Incentives from "@/pages/Incentives";
import EVTym from "@/pages/EVTym";
import Assets from "@/pages/Assets";
import Attendance from "@/pages/Attendance";
import Payouts from "@/pages/Payouts";
import MonthClose from "@/pages/MonthClose";
import Locations from "@/pages/Locations";
import Reports from "@/pages/Reports";
import Onboarding from "@/pages/Onboarding";
import Settings from "@/pages/Settings";
import AuditLogs from "@/pages/AuditLogs";
import FranchisePartners from "@/pages/FranchisePartners";
import TransferHistory from "@/pages/TransferHistory";
import FranchiseProfile from "@/pages/FranchiseProfile";
import FranchiseDashboard from "@/pages/FranchiseDashboard";
import Payments from "@/pages/Payments";
import AdminUsers from "@/pages/AdminUsers";
import WeeklySend from "@/pages/WeeklySend";
import TdsManagement from "@/pages/TdsManagement";
import Certificates from "@/pages/Certificates";
import VerifyCertificate from "@/pages/VerifyCertificate";
import { ReauthDialog } from "@/components/ReauthDialog";
import { visibleNav } from "@/components/Layout";

function NoAccess() {
  return <div className="rounded-xl border border-border bg-card p-10 text-center" data-testid="no-access"><p className="font-head text-lg font-bold">Access restricted</p><p className="mt-1 text-sm text-muted-foreground">You don't have permission to view this page. Contact your Super Admin.</p></div>;
}

function Guard({ path, children }) {
  const { user, canAny } = useAuth();
  const nav = visibleNav(user, canAny);
  if (path === "/" && !nav.some((n) => n.to === "/")) {
    const first = nav[0];
    return first ? <Navigate to={first.to} replace /> : <NoAccess />;
  }
  if (path && !nav.some((n) => n.to === path)) return <NoAccess />;
  return children;
}

function MyFranchise() {
  const { user } = useAuth();
  return user?.franchise_partner_id ? <Navigate to={`/franchise/${user.franchise_partner_id}`} replace /> : <NoAccess />;
}

const G = (path, el) => <Guard path={path}>{el}</Guard>;

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-background text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppShell() {
  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={G("/", <Dashboard />)} />
          <Route path="/my-franchise" element={<MyFranchise />} />
          <Route path="/partners" element={G("/partners", <DeliveryPartners />)} />
          <Route path="/dp/:id" element={G("/partners", <DPProfile />)} />
          <Route path="/operations" element={G("/operations", <DailyOperations />)} />
          <Route path="/earnings" element={G("/earnings", <Earnings />)} />
          <Route path="/incentives" element={G("/incentives", <Incentives />)} />
          <Route path="/ev" element={G("/ev", <EVTym />)} />
          <Route path="/assets" element={G("/assets", <Assets />)} />
          <Route path="/attendance" element={G("/attendance", <Attendance />)} />
          <Route path="/payouts" element={G("/payouts", <Payouts />)} />
          <Route path="/payments" element={G("/payments", <Payments />)} />
          <Route path="/month-close" element={G("/month-close", <MonthClose />)} />
          <Route path="/franchise" element={G("/franchise", <FranchisePartners />)} />
          <Route path="/transfers" element={G("/transfers", <TransferHistory />)} />
          <Route path="/franchise-dashboard" element={G("/franchise-dashboard", <FranchiseDashboard />)} />
          <Route path="/franchise/:id" element={<FranchiseProfile />} />
          <Route path="/locations" element={G("/locations", <Locations />)} />
          <Route path="/reports" element={G("/reports", <Reports />)} />
          <Route path="/reports/franchise" element={<Navigate to="/reports" replace />} />
          <Route path="/onboarding" element={G("/onboarding", <Onboarding />)} />
          <Route path="/settings" element={G("/settings", <Settings />)} />
          <Route path="/audit" element={G("/audit", <AuditLogs />)} />
          <Route path="/tds" element={G("/tds", <TdsManagement />)} />
          <Route path="/certificates" element={G("/certificates", <Certificates />)} />
          <Route path="/weekly-send" element={G("/weekly-send", <WeeklySend />)} />
          <Route path="/admin/users" element={G("/admin/users", <AdminUsers />)} />
        </Routes>
      </Layout>
      <QuickEntryDrawer />
      <AIInsightsDrawer />
      <ReauthDialog />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-certificate/:number" element={<VerifyCertificate />} />
            <Route path="/*" element={<Protected><AppShell /></Protected>} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AppProvider>
    </AuthProvider>
  );
}

export default App;
